import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import _ from 'lodash'
import classnames from 'classnames'
import { HostedField, Braintree } from 'react-braintree-fields'
import dispatchSelector from 'reduxModules/forms/paymentMethod/dispatchers'
import stateSelector from 'reduxModules/forms/paymentMethod//selectors'
import FieldError from './FieldError'
import { CARD_FIELDS, orderedInvalidFields } from './cardFields'
import getCardIcon from '../../utils/getCardIcon'
import keyUpHandler from '../../utils/keyUpHandler'
import { SLATE } from '../../../global/assets/styles/vars'
import './styles.css'

export class PaymentMethodForm extends Component {
  constructor(props) {
    super(props)
    this.fieldRefs = {}
    CARD_FIELDS.forEach(({ key }) => {
      this.fieldRefs[key] = React.createRef()
    })
    // Hosted-field handlers are captured once at mount; read current props
    this.handleValidityChange = this.handleValidityChange.bind(this)
  }

  componentDidUpdate(prevProps) {
    // A new failed submit: take the customer to the first field to fix
    if (this.props.fieldsErrorSeq !== prevProps.fieldsErrorSeq) {
      const [first] = orderedInvalidFields(this.props.invalidFields)
      const field = first && this.fieldRefs[first].current
      if (field) {
        try {
          field.focus()
        } catch (_) {
          // hosted fields not ready: the inline error still shows
        }
        const wrapper = document.querySelector(`.card-field[data-field="${first}"]`)
        if (wrapper && wrapper.scrollIntoView)
          wrapper.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
    }
  }

  componentWillUnmount() {
    // some iOS devices does not close the keyboard after closing the form.
    document.activeElement.blur()
  }
  // Braintree reports each field's validity as the customer types; once a
  // field flagged by the last submit is valid, clear its error
  handleValidityChange(fieldState, event) {
    const key = event && event.emittedBy
    if (fieldState && fieldState.isValid && this.isInvalid(key) && this.props.fieldBecameValid)
      this.props.fieldBecameValid(key)
  }

  isInvalid(key) {
    return _.includes(this.props.invalidFields, key)
  }

  renderField({ key, error }, className, placeholder) {
    return (
      <div
        className={classnames('card-field', { 'is-invalid': this.isInvalid(key) })}
        data-field={key}
      >
        <HostedField
          ref={this.fieldRefs[key]}
          type={key}
          className={className}
          placeholder={placeholder}
          onValidityChange={this.handleValidityChange}
        />
        <FieldError show={this.isInvalid(key)} message={error} />
      </div>
    )
  }

  render() {
    if (this.props.clientToken === '' && !this.props.showForm) return null
    const [numberField, expiryField, cvvField] = CARD_FIELDS
    return (
      <div id="PaymentMethodForm">
        <div>
          <div
            className={classnames('form', { selected: this.props.payWithCreditCard })}
            aria-label="Choose to pay with credit card"
            onClick={() => this.props.choosePayWithCreditCard()}
            onKeyUp={event => keyUpHandler(event, this.props.choosePayWithCreditCard)}
            tabIndex="0"
          >
            {this.props.enablePaypal && (
              <div className="pay-with-card">
                <input
                  aria-label="Pay with credit card"
                  checked={this.props.payWithCreditCard}
                  name="paypal"
                  type="radio"
                  readOnly
                />
                <span>Credit Card</span>
              </div>
            )}
            <div className="card-type">
              <img
                src={getCardIcon(this.props.type)}
                alt={this.props.type}
              />
            </div>
            <Braintree
              authorization={this.props.clientToken}
              getTokenRef={t => this.props.storeTokenizer(t)}
              onCardTypeChange={this.props.cardTypeChanged}
              styles={{
                input: {
                  color: SLATE
                }
              }}
            >
              <div className="payment-container">
                <div className="payment-fields">
                  {this.renderField(numberField, 'hosted-field number', '1234 1234 1234 1234')}
                  <div className="mobile-grouping">
                    {this.renderField(expiryField, 'hosted-field expiration-date', 'MM / YY')}
                    {this.renderField(cvvField, 'hosted-field cvv', 'Security Code')}
                  </div>
                </div>
                {this.props.enableStoreMethodOption && (
                  <div className="optional-saving">
                    <input
                      id="save-payment-method-check"
                      type="checkbox"
                      value={this.props.storeMethod}
                      checked={this.props.storeMethod}
                      onChange={() => this.props.toggleStorePayment()}
                    />
                    <label
                      tabIndex="0"
                      onKeyUp={(event) => keyUpHandler(event, this.props.toggleStorePayment)}
                      htmlFor="save-payment-method-check"
                      onClick={() => this.props.toggleStorePayment()}
                    >
                      Save new payment method
                    </label>
                  </div>
                )}
              </div>
            </Braintree>
          </div>
          {this.props.enablePaypal && (
            <div
              className={classnames('paypal', { selected: !this.props.payWithCreditCard })}
              aria-label="Choose to pay with PayPal"
              onClick={() => this.props.choosePayWithPaypal()}
              onKeyUp={event => keyUpHandler(event, this.props.choosePayWithPaypal)}
              tabIndex="0"
            >
              <div className="inner-container">
                <div className="option">
                  <input
                    aria-label="Pay with Paypal"
                    checked={!this.props.payWithCreditCard}
                    name="paypal"
                    type="radio"
                    readOnly
                  />
                  <span>PayPal</span>
                </div>
                <div className="icon">
                  <img src="https://assets.braintreegateway.com/payment_method_logo/paypal.png" alt="paypal" />
                </div>
              </div>
            </div>
          )}
          <div className="buttons-container">
            <div className="secure-payment">
              <img src="/global/assets/images/icons/securePayment.svg" className="secure-payment-icon" alt="secure payment" />
              <span>secure payment</span>
            </div>
            {this.props.children}
          </div>
        </div>
      </div>
    )
  }
}

PaymentMethodForm.propTypes = {
  cardTypeChanged: PropTypes.func.isRequired,
  savePaymentMethod: PropTypes.func,
  errorMessage: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  clientToken: PropTypes.string.isRequired,
  showForm: PropTypes.bool.isRequired,
  enablePaypal: PropTypes.bool,
  payWithCreditCard: PropTypes.bool
}

PaymentMethodForm.defaultProps = {
  enablePaypal: false,
  payWithCreditCard: true,
  savePaymentMethod: null
}

export default connect(stateSelector, dispatchSelector)(PaymentMethodForm)
